import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useToast } from '../../hooks/use-toast';
import { Lock, AlertCircle } from 'lucide-react';
import { authAPI } from '../../services/api';

const AdminLogin = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await authAPI.login(credentials);
      
      if (response.data.success) {
        // Store tokens securely
        localStorage.setItem('adminAuth', 'true');
        localStorage.setItem('adminToken', response.data.access_token);
        localStorage.setItem('refreshToken', response.data.refresh_token);
        localStorage.setItem('adminUsername', response.data.username);
        
        setIsAuthenticated(true);
        
        toast({
          title: "Login successful",
          description: "Welcome to admin panel"
        });
        navigate('/admin/dashboard');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Invalid credentials";
      setError(errorMessage);
      
      // Check if account is locked
      if (error.response?.status === 423) {
        toast({
          title: "Account Locked",
          description: errorMessage,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Login failed",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-4">
            <div className="relative w-8 h-8">
              <div className="absolute w-6 h-6 rounded-full bg-white" />
              <div className="absolute w-6 h-6 rounded-full bg-white left-3 mix-blend-exclusion" />
            </div>
            <span className="text-3xl font-light text-white">onlzCode</span>
          </div>
          <h1 className="text-2xl font-light text-white mb-2">Admin Panel</h1>
          <p className="text-sm text-gray-400">Sign in to manage your content</p>
        </div>

        <div className="bg-white/5 border border-white/10 p-8 backdrop-blur-sm">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Username</label>
              <Input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                required
                className="w-full bg-transparent border-white/20 focus:border-white text-white"
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-2 uppercase tracking-wider">Password</label>
              <Input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                required
                className="w-full bg-transparent border-white/20 focus:border-white text-white"
                placeholder="Enter password"
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-white text-black hover:bg-gray-200 py-6 text-base transition-all duration-300 group"
            >
              <Lock className="mr-2 w-4 h-4" />
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
            <p className="text-xs text-yellow-400">
              <strong>Security Notice:</strong> After 5 failed login attempts, your account will be temporarily locked for 15 minutes.
            </p>
          </div>

          <div className="mt-4 text-center text-xs text-gray-500">
            <p>Default credentials: admin / admin</p>
            <p className="mt-1 text-yellow-500">Please change your password after first login!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
